import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useFormik, FormikProvider } from 'formik';
import { Box, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import _ from 'lodash';
import * as productApi from '../../lib/api/product';
import TitleBox from '../../components/common/breadcrumbs/TitleBox';
import DetailTemplate from '../../components/common/template/DetailTemplate';
import ProductAddForm from '../../components/productmng/ProductAddForm';
import AskDialog from '../../components/common/dialog/AskDialog';

const ProductAddContainer = () => {
  let navigate = useNavigate();
  const arrayHelperRef = useRef();

  const { enqueueSnackbar } = useSnackbar();
  const [priceTyps, setPriceTyps] = useState([]);
  const [message, setMessage] = useState(
    '기본사항 등록이 완료되지 못했습니다.',
  );
  const [visible, setVisible] = useState(false);
  const onCancle = () => {
    setVisible(false);
  };
  const onConfirm = () => {
    setVisible(false);
  };

  const handleGoBack = () => {
    navigate('/productmng');
  };

  let initialValues = {
    itemCd: '',
    productLine: '',
    productNm: '',
    partnerUuid: '',
    partnerNm: '',
    price: '',
    priceConsume: '',
    category: '',
    productUnit: '',
    productDesc: '',
  };

  const validationSchema = yup.object({
    productNm: yup
      .string('')
      .required('제품명을 입력하세요')
      .test(
        'checkPrdNameUnique',
        '등록된 제품명입니다.',
        async function (value) {
          if (!_.isEmpty(value)) {
            const data = { productNm: value.trim() };
            let isDuplicateExists = false;
            const res = await productApi.checkPrdNameDuplicate(data);
            if (res.data) {
              isDuplicateExists = res.data.duplicate === 'true' ? false : true;
            }

            return isDuplicateExists;
          }
          // WHEN THE VALUE IS EMPTY RETURN `true` by default
          return true;
        },
      ),
    productLine: yup.string().required('제품군을 입력하세요'),
    productSpec: yup.string().required('제품규격을 입력하세요'),
    category: yup.string().required('분류 코드를 선택하세요'),
    partnerNm: yup.string().required('제조사를 선택하세요'),
    price: yup
      .number()
      .typeError('매입 가격은 숫자만 가능합니다.')
      .required('매입 가격을 입력하세요')
      .positive()
      .min(0, '최소 0보다 큰값이어야 합니다'),
    priceConsume: yup
      .number()
      .typeError('소비자 가격은 숫자만 가능합니다.')
      .nullable()
      .positive()
      .min(0, '최소 0보다 큰값이어야 합니다'),
  });

  const onSave = async (dataToSubmit) => {
    formik.setSubmitting(true);
    try {
      const res = await productApi.addProduct(dataToSubmit);
      if (res.data) {
        navigate(`/productmng/${res.data.productUuid}`);
        enqueueSnackbar('제품정보가 추가되었습니다', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    } finally {
      formik.resetForm();
      formik.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      let dataToSubmit = {
        prdTyp1: '-',
        prdTyp2: '-',
        itemCd: values.category, //category와 동일하게 입력
        productLine: values.productLine.trim(),
        productSpec: values.productSpec.trim(),
        productNm: values.productNm.trim(),
        modelNm: values.productNm.trim(), //모델명 제거-productNm 동일하게 넣음.
        partnerUuid: values.partnerUuid,
        category: values.category,
        price: values.price,
        priceConsume: values.priceConsume,
        productUnit: values.productUnit,
        productDesc: values.productDesc,
        useYn: 'Y',
      };

      //console.log(JSON.stringify(dataToSubmit), 'dataToSubmit');
      onSave(dataToSubmit);
    },
  });

  return (
    <Box>
      <TitleBox
        handleGoBack={handleGoBack}
        menuName="제품 관리/제품 관리/추가"
        menuLink="/productmng"
        icon="product"
        titleData="제품 신규 추가"
      />
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: 1,
          px: 2,
        }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            my: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            저장
          </Button>
        </Box>
        <DetailTemplate title="기본정보">
          <ProductAddForm formik={formik} />
        </DetailTemplate>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            my: 2,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            저장
          </Button>
        </Box>
      </Box>
      <AskDialog
        visible={visible}
        title="생성 오류"
        description={message}
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
    </Box>
  );
};

export default ProductAddContainer;
